import { memo, useMemo } from 'react';
import styled from 'styled-components';
import PropTypes from 'prop-types';
import { components } from 'react-select';
import { Link } from 'react-router-dom';
import AutocompleteField from '../components/AutocompleteField';
import config from '../_config/config';
import history from '../helpers/history';
import { getUserData } from '../_actions';
import { canSeeFacilitySearch } from '../helpers';

import { ReactComponent as PlusIcon } from '../assets/images/plus-fuel.svg';
import ButtonIconDropdown from '../components/ButtonIconDropdown';
import { ReactComponent as QuestionMarkIcon } from '../assets/images/question-mark.svg';
import Banner from './Banner';

const HeaderLink = ({ href, iClass, text }) => {
  const linkClasses = `${iClass} text-primary`;
  return (
    <Link to={href} className="btn btn-link btn-float text-default">
      <i className={linkClasses} />
      <span>{text}</span>
    </Link>
  );
};

HeaderLink.propTypes = {
  href: PropTypes.string.isRequired,
  iClass: PropTypes.string.isRequired,
  text: PropTypes.string.isRequired
};

const SearchWrapper = styled.div`
  width: 330px;
  max-width: 330px;
  min-width: 330px;
  margin-right: 40px;
  font-size: 13px;
  font-weight: 500;
  color: #a4a8b1;
  .px-3 {
    padding-right: 0 !important;
    div {
      div {
        span {
          display: none;
        }
      }
    }
  }
`;

const Item = ({ innerProps, data }) => {
  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <div {...innerProps} className="custom-select-menu">
      {`(${data.abbreviation}: ${data.facility_id || 'None'})`}
      <strong>{` ${data.name} `}</strong>
      {`${data.address}, ${data.city}, ${data.state}`}
    </div>
  );
};

Item.propTypes = {
  innerProps: PropTypes.instanceOf(Object).isRequired,
  data: PropTypes.instanceOf(Object).isRequired
};

const MenuList = props => {
  return (
    // eslint-disable-next-line react/jsx-props-no-spreading
    <components.MenuList {...props} className="protean-scroll">
      {/* eslint-disable-next-line */}
      {props.children}
    </components.MenuList>
  );
};

const PageHeader = ({ renderCustomPath, showSubHeader }) => {
  const pushToFacility = siteId => {
    history.push(`/facility_profile/${siteId}`);
  };
  const user = getUserData();

  const magicButton = useMemo(() => {
    // eslint-disable-next-line
    const { features, feature_toggles, roles } = getUserData();
    const vendor =
      features?.includes('vendor.read') &&
      // eslint-disable-next-line
      (feature_toggles?.quick_dispatch_enabled || feature_toggles?.inventory_management_enabled);
    const res = {
      show: false,
      items: []
    };
    if (vendor) {
      res.items.push({ Icon: () => <></>, id: 'vendors', filename: 'Manage Vendors' });
      res.show = true;
    }
    if (roles.includes('Owner') || roles.includes('Developer') || roles.includes('Manager')) {
      res.items.push({
        Icon: () => <></>,
        id: 'photoResearch',
        filename: 'Facility Location Overview (by Google)'
      });
      res.show = true;
    }
    return res;
  }, []);

  const getLabel = v =>
    `(${v.abbreviation}: ${v.facility_id}){{SPLIT}} ${v.name} {{SPLIT}} ${v.address}, ${v.city}, ${v.state}`;

  const isSearch = canSeeFacilitySearch(user);
  return (
    <div
      className={`page-header page-header-light page-header-new ${!showSubHeader && 'no-extra'}`}
    >
      <div className="breadcrumb-line breadcrumb-line-light header-elements-md-inline">
        <div className="d-flex">
          <div className="breadcrumb">{renderCustomPath()}</div>
        </div>
        <div className="header-elements d-none">
          {isSearch && (
            <SearchWrapper className="col-md-4 px-0">
              <AutocompleteField
                path={`${config.api.api_url}${config.api.profile.filter_sites}`}
                title="Facility"
                placeholder="Search by Facility"
                initStyles={{
                  control: provided => ({
                    ...provided,
                    minHeight: '30px',
                    height: '30px'
                  }),
                  valueContainer: provided => ({
                    ...provided,
                    height: '30px',
                    padding: '0 6px'
                  }),
                  container: provided => ({
                    ...provided,
                    width: '-webkit-fill-available'
                  }),
                  menu: provided => ({
                    ...provided,
                    zIndex: 99,
                    width: 'calc(100% - 1.4em)'
                  }),
                  indicatorsContainer: provided => ({
                    ...provided,
                    height: '30px'
                  })
                }}
                isHeader
                getValue={v => pushToFacility(v)}
                getCustomOptionLabel={getLabel}
                extraEomponents={{ Option: Item, MenuList }}
              />
            </SearchWrapper>
          )}
          <div className="breadcrumb justify-content-center support-btn">
            <a
              href="mailto:support@proteanapp.com "
              className="breadcrumb-elements-item d-inline-flex align-items-center"
            >
              <QuestionMarkIcon className="mr-8" />
              Support
            </a>
            {/* <SettingsWindow /> */}
          </div>
          {magicButton.show && (
            <ButtonIconDropdown
              Icon={PlusIcon}
              extraClass="magic-button mr-2"
              items={magicButton.items}
              iconH={32}
              iconW={32}
              onClick={id => history.push(`/${id}`)}
            />
          )}
        </div>
      </div>
      <Banner />
    </div>
  );
};

PageHeader.propTypes = {
  renderCustomPath: PropTypes.func,
  showSubHeader: PropTypes.bool
};

PageHeader.defaultProps = {
  renderCustomPath: null,
  showSubHeader: false
};

const PageHeaderWrapper = memo(PageHeader);
export default PageHeaderWrapper;
